// Attendance · Needs Review
//
// A standalone page — deliberately its own file, not a tab bolted onto the
// existing AttendanceDashboard.js. When face recognition can't place an
// OFFLINE-captured punch, the mobile app hands the photo to
// POST /attendance/review-queue instead of destroying it (the FO has gone
// home and cannot retake it). This page is where office staff identify the
// person and approve or reject that photo.
//
// Backend: sowash-backend/routes/attendanceReviewRoutes.js — full design
// notes and status in sowash-backend/docs/attendance-review-queue.md.

import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { fmtPKTDateTime } from '../../helpers/time';
import { notify } from '../../helpers/utils';

// ─── DESIGN TOKENS (same NOVAA dark palette as AttendanceDashboard.js —
// duplicated rather than imported: that file exports nothing, and this page
// is deliberately kept independent of it) ──────────────────────────────
const T = {
  bg: 'var(--nd-bg)',
  surface: 'var(--nd-surface)',
  border: 'var(--nd-w06)',
  text: 'var(--nd-text)',
  textStrong: 'var(--nd-text-strong)',
  textMuted: 'var(--nd-tx35)',
  blue: '#3B82F6',
  blueLight: '#60A5FA',
  green: '#22D3A5',
  amber: '#F59E0B',
  red: '#F87171',
};

const API = process.env.REACT_APP_API_BASE_URL;
const IMG_BASE = API ? API.replace('/api', '') : '';

const GLOBAL = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&display=swap');
  .nv-arq * { box-sizing: border-box; }
  .nv-arq ::-webkit-scrollbar { width: 7px; height: 7px; }
  .nv-arq ::-webkit-scrollbar-thumb { background: var(--nd-w08); border-radius: 4px; }
  .nv-arq ::-webkit-scrollbar-track { background: transparent; }

  .nv-arq input, .nv-arq select, .nv-arq textarea {
    background: var(--nd-w05); border: 1px solid var(--nd-w10); border-radius: 8px;
    color: var(--nd-text); font-family: 'DM Sans', sans-serif; font-size: 13px;
    padding: 8px 12px; outline: none;
  }
  .nv-arq input:focus, .nv-arq select:focus, .nv-arq textarea:focus { border-color: rgba(96,165,250,0.5); }
  .nv-arq input[type="date"] { color-scheme: dark; }

  .nv-arq .nv-tab {
    padding: 14px 18px; background: none; border: none; border-bottom: 2px solid transparent;
    color: var(--nd-tx35); font-family: 'DM Sans', sans-serif; font-weight: 700; font-size: 12.5px;
    cursor: pointer; transition: color .15s, border-color .15s; white-space: nowrap;
  }
  .nv-arq .nv-tab:hover { color: var(--nd-text); }
  .nv-arq .nv-tab.nv-on { color: var(--nd-text-strong); border-bottom-color: #3B82F6; }

  .nv-arq .nv-fieldlabel { font-size: 8.5px; letter-spacing: 0.16em; color: var(--nd-tx35); font-weight: 700; margin-bottom: 6px; }
`;

const STATUS_TABS = [
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'all', label: 'All' },
];

const pad = (n) => String(n).padStart(2, '0');
const todayStr = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };

// The device stamps `reason` with values like "face_not_recognised",
// "http_404", or "face_not_recognised_http_422" — and appends
// "_legacy_unknown_connectivity" for punches queued before capturedOffline
// existed on the record (see AttendanceBackgroundSync.ts). Turn that into
// something a reviewer can read at a glance.
const humanizeReason = (reason) => {
  if (!reason) return { text: 'Reason not recorded', legacy: false };
  const legacy = reason.endsWith('_legacy_unknown_connectivity');
  const base = legacy ? reason.slice(0, -'_legacy_unknown_connectivity'.length) : reason;
  if (base === 'face_not_recognised') return { text: 'Face not recognised', legacy };
  const httpMatch = base.match(/^(?:face_not_recognised_)?http_(\d+)$/);
  if (httpMatch) return { text: `Upload failed (HTTP ${httpMatch[1]})`, legacy };
  return { text: base.replace(/_/g, ' '), legacy };
};

const imgSrc = (u) => (/^(data:|blob:|https?:)/i.test(u) ? u : `${IMG_BASE}${u}`);

const userLabel = (u) => {
  const parts = [u.name || `User #${u.id}`];
  const tag = [u.role, u.department].filter(Boolean).join(' · ');
  if (tag) parts.push(`— ${tag}`);
  return parts.join(' ');
};

const Field = ({ label, children }) => (
  <div>
    <div className="nv-fieldlabel">{label}</div>
    {children}
  </div>
);

// ─── One review card: photo, context, person picker, approve/reject ──────
const ReviewCard = ({ row, users, busy, onApprove, onReject, onEnlarge, onQuickAdd }) => {
  const [userId, setUserId] = useState('');
  const [query, setQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [creating, setCreating] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [note, setNote] = useState('');

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return users.filter(u => (u.name || '').toLowerCase().includes(q)).slice(0, 8);
  }, [query, users]);

  const pickUser = (u) => {
    setUserId(u.id);
    setQuery(u.name || userLabel(u));
    setShowSuggestions(false);
  };

  const handleQuickAdd = async () => {
    const name = query.trim();
    if (!name || creating) return;
    setCreating(true);
    const newUser = await onQuickAdd(name);
    setCreating(false);
    if (newUser) pickUser(newUser);
  };
  const reason = humanizeReason(row.reason);
  const isPending = row.status === 'pending';

  const statusColor = row.status === 'approved' ? T.green : row.status === 'rejected' ? T.red : T.amber;

  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: 14, display: 'flex', gap: 14 }}>
      <img
        src={imgSrc(row.image_url)}
        alt="Attendance capture"
        loading="lazy"
        decoding="async"
        onClick={() => onEnlarge(imgSrc(row.image_url))}
        style={{ width: 92, height: 92, borderRadius: 8, objectFit: 'cover', cursor: 'pointer', border: '1px solid var(--nd-w12)', flexShrink: 0 }}
        title="Click to enlarge"
      />

      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{
            fontSize: 9, fontWeight: 800, letterSpacing: '0.06em', padding: '3px 8px', borderRadius: 5,
            color: row.action === 'clock_out' ? T.red : T.green,
            background: row.action === 'clock_out' ? 'rgba(248,113,113,0.12)' : 'rgba(34,211,165,0.12)',
          }}>
            {row.action === 'clock_out' ? 'CLOCK OUT' : 'CLOCK IN'}
          </span>
          <span style={{ fontSize: 12, color: T.textMuted, fontFamily: "'DM Mono', monospace" }}>{fmtPKTDateTime(row.captured_at)}</span>
          {row.status !== 'pending' && (
            <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.06em', color: statusColor, marginLeft: 'auto' }}>
              {row.status.toUpperCase()}
            </span>
          )}
        </div>

        <div style={{ fontSize: 12.5, color: T.text }}>
          {row.client_name || <span style={{ color: 'var(--nd-w20)' }}>No client linked</span>}
          {row.site_name && <span style={{ color: T.textMuted }}> · {row.site_name}</span>}
        </div>

        <div style={{ fontSize: 11, color: T.textMuted, display: 'flex', alignItems: 'center', gap: 6 }}>
          {reason.text}
          {reason.legacy && (
            <span
              title="Queued before this device recorded whether it was offline — treated as offline to be safe."
              style={{ fontSize: 8.5, fontWeight: 800, color: T.textMuted, border: `1px solid ${T.border}`, padding: '1px 6px', borderRadius: 4 }}
            >
              connectivity unknown
            </span>
          )}
        </div>

        {row.status === 'approved' && row.fo_name && (
          <div style={{ fontSize: 12, color: T.green }}>Recorded for <strong>{row.fo_name}</strong>{row.reviewed_by_name ? ` · by ${row.reviewed_by_name}` : ''}</div>
        )}
        {row.status === 'rejected' && (
          <div style={{ fontSize: 12, color: T.textMuted }}>Rejected{row.reviewed_by_name ? ` by ${row.reviewed_by_name}` : ''}</div>
        )}

        {isPending && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 2 }}>
            {!rejecting ? (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => { setQuery(e.target.value); setUserId(''); setShowSuggestions(true); }}
                    onFocus={() => setShowSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                    placeholder="Who is this? Type a name…"
                    disabled={busy}
                    style={{ width: '100%' }}
                  />
                  {showSuggestions && query.trim() && (
                    <div style={{
                      position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, zIndex: 20,
                      background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8,
                      maxHeight: 220, overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
                    }}>
                      {suggestions.map(u => (
                        <div
                          key={u.id}
                          onMouseDown={() => pickUser(u)}
                          style={{ padding: '8px 12px', fontSize: 12.5, color: T.text, cursor: 'pointer' }}
                        >
                          {userLabel(u)}
                        </div>
                      ))}
                      {suggestions.length === 0 && (
                        <div
                          onMouseDown={handleQuickAdd}
                          style={{ padding: '8px 12px', fontSize: 12.5, color: T.amber, cursor: creating ? 'not-allowed' : 'pointer' }}
                        >
                          {creating ? 'Adding…' : `No match — add "${query.trim()}" as a new employee`}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => onApprove(row.id, userId)}
                  disabled={busy || !userId}
                  style={{
                    padding: '8px 14px', background: (!userId || busy) ? 'var(--nd-w05)' : 'linear-gradient(135deg,#3B82F6,#22D3A5)',
                    border: 'none', borderRadius: 7, color: (!userId || busy) ? 'var(--nd-w20)' : '#fff',
                    cursor: (!userId || busy) ? 'not-allowed' : 'pointer', fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap',
                  }}
                >
                  {busy ? 'Working…' : '✓ Approve'}
                </button>
                <button
                  onClick={() => setRejecting(true)}
                  disabled={busy}
                  style={{ padding: '8px 14px', background: 'none', border: `1px solid ${T.border}`, borderRadius: 7, color: busy ? 'var(--nd-w20)' : T.textMuted, cursor: busy ? 'not-allowed' : 'pointer', fontSize: 11.5, fontWeight: 700 }}
                >
                  ✕ Reject
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Why? (optional — e.g. blurry / not one of ours)"
                  rows={2}
                  disabled={busy}
                  style={{ resize: 'vertical', fontFamily: "'DM Sans', sans-serif" }}
                />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    onClick={() => onReject(row.id, note)}
                    disabled={busy}
                    style={{ padding: '7px 12px', background: 'rgba(248,113,113,0.14)', border: '1px solid rgba(248,113,113,0.35)', borderRadius: 6, color: T.red, cursor: busy ? 'not-allowed' : 'pointer', fontSize: 11, fontWeight: 700 }}
                  >
                    {busy ? 'Working…' : 'Confirm reject'}
                  </button>
                  <button
                    onClick={() => { setRejecting(false); setNote(''); }}
                    disabled={busy}
                    style={{ padding: '7px 12px', background: 'none', border: `1px solid ${T.border}`, borderRadius: 6, color: T.textMuted, cursor: busy ? 'not-allowed' : 'pointer', fontSize: 11, fontWeight: 700 }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const AttendanceReviewQueue = () => {
  const [status, setStatus] = useState('pending');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [users, setUsers] = useState([]);
  const [lightbox, setLightbox] = useState(null);

  const LIMIT = 60;
  const [offset, setOffset] = useState(0);

  const fetchQueue = async (nextOffset = 0) => {
    try {
      setLoading(true); setError(null);
      const qs = new URLSearchParams({ status, limit: String(LIMIT), offset: String(nextOffset) });
      if (from) qs.append('from', from);
      if (to) qs.append('to', to);
      const res = await axios.get(`${API}/attendance/review-queue?${qs.toString()}`);
      const list = res.data?.records || [];
      setRecords(nextOffset === 0 ? list : (prev) => [...prev, ...list]);
      setTotal(res.data?.total ?? 0);
      setOffset(nextOffset);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load the review queue');
      if (nextOffset === 0) setRecords([]);
    } finally { setLoading(false); }
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/schedule/users/available`);
      const list = (res.data?.users || []).slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setUsers(list);
    } catch (e) { setUsers([]); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchQueue(0); }, [status, from, to]);
  useEffect(() => { fetchUsers(); }, []);

  const handleApprove = async (id, userId) => {
    if (!userId) return;
    try {
      setBusyId(id);
      const res = await axios.post(`${API}/attendance/review-queue/${id}/approve`, { user_id: Number(userId) });
      notify('success', res.data?.already_present ? `Matched an existing record for ${res.data.fo_name}` : `Approved for ${res.data?.fo_name || 'the selected person'}`);
      setRecords(prev => prev.filter(r => r.id !== id));
      setTotal(t => Math.max(0, t - 1));
    } catch (err) {
      const data = err.response?.data;
      notify('error', data?.message || 'Could not approve this punch');
      // 409 (already adjudicated / being processed / recorded under someone
      // else) and 422 (bad action / no open shift) all mean the row's real
      // state has moved on from what's on screen — refetch rather than leave
      // a stale card showing.
      fetchQueue(0);
    } finally { setBusyId(null); }
  };

  const handleReject = async (id, note) => {
    try {
      setBusyId(id);
      await axios.post(`${API}/attendance/review-queue/${id}/reject`, { note });
      notify('success', 'Rejected');
      setRecords(prev => prev.filter(r => r.id !== id));
      setTotal(t => Math.max(0, t - 1));
    } catch (err) {
      const data = err.response?.data;
      // A 409 here can mean the punch was actually already recorded (the
      // server self-heals it to approved instead of destroying it) — surface
      // that plainly rather than a generic failure message.
      notify('error', data?.message || 'Could not reject this punch');
      fetchQueue(0);
    } finally { setBusyId(null); }
  };

  // Person in the photo isn't in `users` yet — create a bare employee record so
  // approval still goes through the normal user_id path (see the backend route
  // for why: keeps future occurrences of this person findable/reusable instead
  // of a free-typed string that drifts from itself across attendance rows).
  const handleQuickAddUser = async (name) => {
    try {
      const res = await axios.post(`${API}/attendance/review-queue/quick-add-user`, { name });
      const newUser = res.data?.user;
      if (newUser) {
        setUsers(prev => [...prev, newUser].sort((a, b) => (a.name || '').localeCompare(b.name || '')));
      }
      return newUser || null;
    } catch (err) {
      notify('error', err.response?.data?.message || 'Could not add this person');
      return null;
    }
  };

  const card = { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, overflow: 'hidden' };
  const hasMore = records.length < total;

  const pendingCountLabel = useMemo(() => {
    if (status !== 'pending') return null;
    if (loading && records.length === 0) return '…';
    return String(total);
  }, [status, loading, total, records.length]);

  return (
    <div className="nv-arq" style={{ background: T.bg, fontFamily: "'DM Sans', sans-serif", color: T.text, padding: '14px 18px', minHeight: '100vh' }}>
      <style>{GLOBAL}</style>

      <div style={{ ...card, marginBottom: 12 }}>
        <div style={{ padding: '20px 24px 18px' }}>
          <div style={{ fontSize: 9, color: T.textMuted, letterSpacing: '0.18em', marginBottom: 6 }}>NOVAA · C&I MANAGEMENT</div>
          <div style={{ fontSize: 27, fontWeight: 800, color: T.textStrong, letterSpacing: '-0.03em', lineHeight: 1 }}>
            Attendance · Needs Review
            {pendingCountLabel !== null && (
              <span style={{ marginLeft: 12, fontSize: 13, fontWeight: 700, color: T.amber, verticalAlign: 'middle' }}>{pendingCountLabel} pending</span>
            )}
          </div>
          <div style={{ fontSize: 12.5, color: T.textMuted, marginTop: 8 }}>
            Punches captured offline where face recognition couldn't identify the person. The FO has already left
            site and can't retake these — pick who it is, or reject if the photo is unusable.
          </div>
        </div>
        <div style={{ height: 2, background: 'linear-gradient(90deg,#3B82F6,#22D3A5,transparent)' }} />

        <div style={{ padding: '14px 24px 16px', display: 'flex', alignItems: 'flex-end', gap: 18, flexWrap: 'wrap' }}>
          <Field label="STATUS">
            <div style={{ display: 'flex', gap: 4, background: 'var(--nd-w04)', padding: 3, borderRadius: 9, border: `1px solid ${T.border}` }}>
              {STATUS_TABS.map(s => (
                <button key={s.key} onClick={() => setStatus(s.key)}
                  style={{ padding: '7px 13px', background: status === s.key ? 'linear-gradient(135deg,#3B82F6,#22D3A5)' : 'transparent', color: status === s.key ? T.textStrong : T.textMuted, border: 'none', borderRadius: 7, fontWeight: 700, cursor: 'pointer', fontSize: 11.5, fontFamily: "'DM Sans', sans-serif" }}>
                  {s.label}
                </button>
              ))}
            </div>
          </Field>

          <Field label="CAPTURED">
            <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <input type="date" value={from} onChange={e => setFrom(e.target.value)} max={to || todayStr()} />
              <span style={{ color: T.textMuted, fontSize: 12 }}>→</span>
              <input type="date" value={to} onChange={e => setTo(e.target.value)} min={from} max={todayStr()} />
              {(from || to) && (
                <button onClick={() => { setFrom(''); setTo(''); }}
                  style={{ padding: '8px 10px', background: 'none', border: `1px solid ${T.border}`, borderRadius: 7, color: T.textMuted, cursor: 'pointer', fontSize: 11 }}>
                  ✕
                </button>
              )}
            </div>
          </Field>

          <button onClick={() => fetchQueue(0)} style={{ marginLeft: 'auto', padding: '9px 14px', background: 'var(--nd-w05)', border: `1px solid ${T.border}`, borderRadius: 8, color: T.textMuted, cursor: 'pointer', fontSize: 11.5, fontWeight: 700 }}>↻ Refresh</button>
        </div>
      </div>

      {error && <div style={{ ...card, padding: '14px 20px', marginBottom: 12, color: T.red, fontSize: 13 }}>⚠ {error}</div>}

      <div style={{ ...card, padding: '16px 18px' }}>
        {loading && records.length === 0 ? (
          <div style={{ textAlign: 'center', color: T.textMuted, padding: '48px' }}>Loading…</div>
        ) : records.length === 0 ? (
          <div style={{ textAlign: 'center', color: T.textMuted, padding: '48px', fontSize: 13 }}>
            {status === 'pending' ? 'Nothing needs review right now.' : `No ${status} punches for these filters.`}
          </div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: 12 }}>
              {records.map(row => (
                <ReviewCard
                  key={row.id}
                  row={row}
                  users={users}
                  busy={busyId === row.id}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  onEnlarge={setLightbox}
                  onQuickAdd={handleQuickAddUser}
                />
              ))}
            </div>
            {hasMore && (
              <div style={{ textAlign: 'center', marginTop: 16 }}>
                <button onClick={() => fetchQueue(offset + LIMIT)} disabled={loading}
                  style={{ padding: '9px 18px', background: 'var(--nd-w05)', border: `1px solid ${T.border}`, borderRadius: 8, color: T.textMuted, cursor: loading ? 'not-allowed' : 'pointer', fontSize: 11.5, fontWeight: 700 }}>
                  {loading ? 'Loading…' : `Load more (${records.length} of ${total})`}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {lightbox && (
        <div onClick={() => setLightbox(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: 24 }}>
          <img src={lightbox} alt="Attendance capture, enlarged" style={{ maxWidth: '90vw', maxHeight: '88vh', borderRadius: 12, border: '1px solid var(--nd-w15)' }} />
        </div>
      )}
    </div>
  );
};

export default AttendanceReviewQueue;
